import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import AttributeValueEditor from './AttributeValueEditor';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeAttrValue } from '../../utils/blockCalendarAttributeUtils';
import api from '../../utils/axiosConfig';

export default function BlockAttributeValuePopover({
  attr,
  value,
  editable,
  targetType,
  microId,
  phaseId,
  blockId,
  children,
  onSaved,
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(value);

  const save = async (nextVal) => {
    if (!editable) return;
    setSaving(true);
    try {
      const normalized = normalizeAttrValue(nextVal, attr);
      if (targetType === 'micro' && microId) {
        await api.put(`/api/training/periodization/microcycles/${microId}`, {
          attributes: { [attr.type_key]: normalized },
        });
      } else if (targetType === 'phase' && phaseId) {
        await api.put(`/api/training/periodization/phases/${phaseId}`, {
          attributes: { [attr.type_key]: normalized },
        });
      } else if (targetType === 'block' && blockId) {
        const blockRes = await api.get(`/api/training/periodization/blocks/${blockId}`);
        const current = blockRes.data?.block?.attributes || {};
        await api.put(`/api/training/periodization/blocks/${blockId}`, {
          attributes: { ...current, [attr.type_key]: normalized },
        });
      }
      setOpen(false);
      await onSaved?.();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!editable) {
    return children;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraft(value);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-md transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64' align='center' onClick={(e) => e.stopPropagation()}>
        <AttributeValueEditor type={attr} value={draft} onChange={setDraft} />
        <div className='mt-3 flex gap-2'>
          <Button type='button' size='sm' className='flex-1' disabled={saving} onClick={() => save(draft)}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button type='button' size='sm' variant='ghost' disabled={saving} onClick={() => setOpen(false)}>
            {t('common.close')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
